import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit, Trash2, Search, Filter, FileText, Image, Upload, Link, FolderOpen, Crown, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import FileUploadSection from './FileUploadSection';
import WebLinkSection from './WebLinkSection';
import ExtractedQuestionsPreview from './ExtractedQuestionsPreview';
import CategoryManager from './CategoryManager';
import { OCRResult, ExtractedQuestion } from '@/services/ocrService';

interface Question {
  id: number;
  teacher_id: number;
  question_text: string;
  answer_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  subject: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tags: string[];
  source_type: 'manual' | 'pdf' | 'screenshot' | 'weblink' | 'bulk_import';
  source_data: any;
  created_at: string;
  updated_at: string;
}

const QuestionsPoolManager: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [extractedResult, setExtractedResult] = useState<OCRResult | null>(null);
  const [activeTab, setActiveTab] = useState('pool');

  // Multi-selection state
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [isDeleteMultipleOpen, setIsDeleteMultipleOpen] = useState(false);
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);

  const { toast } = useToast();
  const { hasAIAccess } = useAuth();

  // Form state for adding/editing questions
  const [formData, setFormData] = useState({
    question_text: '',
    answer_text: '',
    question_type: 'multiple_choice' as Question['question_type'],
    subject: '',
    difficulty: 'medium' as Question['difficulty'],
    tags: '',
    options: ['', '', '', ''] // for multiple choice
  });

  // Table ID for questions_pool
  const QUESTIONS_POOL_TABLE_ID = '16057';

  useEffect(() => {
    fetchQuestions();
  }, [currentPage, filterSubject, filterDifficulty, filterType]);

  useEffect(() => {
    filterQuestions();
  }, [questions, searchTerm]);

  // Clear selections when questions change
  useEffect(() => {
    setSelectedQuestions([]);
  }, [questions]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);

      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const filters = [
      { name: 'teacher_id', op: 'Equal', value: userInfo.ID }];


      if (filterSubject && filterSubject !== 'all') {
        filters.push({ name: 'subject', op: 'StringContains', value: filterSubject });
      }
      if (filterDifficulty && filterDifficulty !== 'all') {
        filters.push({ name: 'difficulty', op: 'Equal', value: filterDifficulty });
      }
      if (filterType && filterType !== 'all') {
        filters.push({ name: 'question_type', op: 'Equal', value: filterType });
      }

      const { data, error } = await window.ezsite.apis.tablePage(QUESTIONS_POOL_TABLE_ID, {
        PageNo: currentPage,
        PageSize: 10,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: filters
      });

      if (error) throw error;

      const questionsData = data.List.map((q: any) => ({
        ...q,
        tags: q.tags ? JSON.parse(q.tags) : [],
        source_data: q.source_data ? JSON.parse(q.source_data) : {}
      }));

      setQuestions(questionsData);
      setTotalPages(Math.ceil(data.VirtualCount / 10));

    } catch (error) {
      console.error('Error fetching questions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch questions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterQuestions = () => {
    if (!searchTerm) {
      setFilteredQuestions(questions);
      return;
    }

    const filtered = questions.filter((question) =>
    question.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    question.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    question.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredQuestions(filtered);
  };

  const handleSelectQuestion = (questionId: number, checked: boolean) => {
    setSelectedQuestions((prev) => {
      if (checked) {
        return [...prev, questionId];
      } else {
        return prev.filter((id) => id !== questionId);
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuestions(filteredQuestions.map((q) => q.id));
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleDeleteMultiple = async () => {
    try {
      setIsDeletingMultiple(true);

      // Delete all selected questions
      const deletePromises = selectedQuestions.map((questionId) =>
      window.ezsite.apis.tableDelete(QUESTIONS_POOL_TABLE_ID, { id: questionId })
      );

      const results = await Promise.all(deletePromises);

      // Check for any errors
      const failedDeletes = results.filter((result) => result.error);

      if (failedDeletes.length > 0) {
        throw new Error(`Failed to delete ${failedDeletes.length} questions`);
      }

      toast({
        title: "Success",
        description: `Successfully deleted ${selectedQuestions.length} questions`
      });

      setSelectedQuestions([]);
      setIsDeleteMultipleOpen(false);
      fetchQuestions();

    } catch (error) {
      console.error('Error deleting multiple questions:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete questions",
        variant: "destructive"
      });
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  const handleAddQuestion = async () => {
    try {
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const questionData = {
        teacher_id: userInfo.ID,
        question_text: formData.question_text,
        answer_text: formData.answer_text,
        question_type: formData.question_type,
        subject: formData.subject,
        difficulty: formData.difficulty,
        tags: JSON.stringify(formData.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag)),
        source_type: 'manual',
        source_data: JSON.stringify(
          formData.question_type === 'multiple_choice' ?
          { options: formData.options.filter((opt) => opt.trim()) } :
          {}
        ),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error } = await window.ezsite.apis.tableCreate(QUESTIONS_POOL_TABLE_ID, questionData);
      if (error) throw error;

      toast({
        title: "Success",
        description: "Question added successfully"
      });

      resetForm();
      setIsAddDialogOpen(false);
      fetchQuestions();

    } catch (error) {
      console.error('Error adding question:', error);
      toast({
        title: "Error",
        description: "Failed to add question",
        variant: "destructive"
      });
    }
  };

  const handleEditQuestion = async () => {
    if (!editingQuestion) return;

    try {
      const questionData = {
        id: editingQuestion.id,
        question_text: formData.question_text,
        answer_text: formData.answer_text,
        question_type: formData.question_type,
        subject: formData.subject,
        difficulty: formData.difficulty,
        tags: JSON.stringify(formData.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag)),
        source_data: JSON.stringify(
          formData.question_type === 'multiple_choice' ?
          { options: formData.options.filter((opt) => opt.trim()) } :
          editingQuestion.source_data
        ),
        updated_at: new Date().toISOString()
      };

      const { error } = await window.ezsite.apis.tableUpdate(QUESTIONS_POOL_TABLE_ID, questionData);
      if (error) throw error;

      toast({
        title: "Success",
        description: "Question updated successfully"
      });

      resetForm();
      setEditingQuestion(null);
      fetchQuestions();

    } catch (error) {
      console.error('Error updating question:', error);
      toast({
        title: "Error",
        description: "Failed to update question",
        variant: "destructive"
      });
    }
  };

  const handleDeleteQuestion = async (questionId: number) => {
    try {
      const { error } = await window.ezsite.apis.tableDelete(QUESTIONS_POOL_TABLE_ID, { id: questionId });
      if (error) throw error;

      toast({
        title: "Success",
        description: "Question deleted successfully"
      });

      fetchQuestions();

    } catch (error) {
      console.error('Error deleting question:', error);
      toast({
        title: "Error",
        description: "Failed to delete question",
        variant: "destructive"
      });
    }
  };

  const handleSaveExtractedQuestions = async (extractedQuestions: ExtractedQuestion[]) => {
    try {
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      // Prepare questions for batch insert
      const questionsToSave = extractedQuestions.map((q) => ({
        teacher_id: userInfo.ID,
        question_text: q.question_text,
        answer_text: q.answer_text,
        question_type: q.question_type,
        subject: q.subject,
        difficulty: q.difficulty,
        tags: JSON.stringify([]),
        source_type: extractedResult?.sourceType || 'pdf',
        source_data: JSON.stringify({
          fileName: extractedResult?.fileName,
          extractedText: extractedResult?.extractedText?.substring(0, 1000), // Store first 1000 chars
          options: q.options || []
        }),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      // Save questions one by one
      for (const questionData of questionsToSave) {
        const { error } = await window.ezsite.apis.tableCreate(QUESTIONS_POOL_TABLE_ID, questionData);
        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Successfully saved ${questionsToSave.length} questions to the pool`
      });

      setExtractedResult(null);
      fetchQuestions();

    } catch (error) {
      console.error('Error saving extracted questions:', error);
      toast({
        title: "Error",
        description: "Failed to save extracted questions",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: '',
      answer_text: '',
      question_type: 'multiple_choice',
      subject: '',
      difficulty: 'medium',
      tags: '',
      options: ['', '', '', '']
    });
  };

  const openEditDialog = (question: Question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      answer_text: question.answer_text,
      question_type: question.question_type,
      subject: question.subject,
      difficulty: question.difficulty,
      tags: question.tags.join(', '),
      options: question.source_data?.options || ['', '', '', '']
    });
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice':return 'bg-blue-100 text-blue-800';
      case 'true_false':return 'bg-green-100 text-green-800';
      case 'short_answer':return 'bg-yellow-100 text-yellow-800';
      case 'essay':return 'bg-purple-100 text-purple-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':return 'bg-green-100 text-green-800';
      case 'medium':return 'bg-yellow-100 text-yellow-800';
      case 'hard':return 'bg-red-100 text-red-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'pdf':return <FileText className="h-4 w-4 text-red-500" data-id="pqnexaypa" data-path="src/components/questions/QuestionsPoolManager.tsx" />;
      case 'screenshot':return <Image className="h-4 w-4 text-blue-500" data-id="fz0bval8c" data-path="src/components/questions/QuestionsPoolManager.tsx" />;
      case 'weblink':return <Link className="h-4 w-4 text-green-500" data-id="iop2y9ll0" data-path="src/components/questions/QuestionsPoolManager.tsx" />;
      case 'bulk_import':return <Upload className="h-4 w-4 text-purple-500" data-id="xxmkqt302" data-path="src/components/questions/QuestionsPoolManager.tsx" />;
      case 'manual':return null;
      default:return <Upload className="h-4 w-4 text-gray-500" data-id="ijwrgr75e" data-path="src/components/questions/QuestionsPoolManager.tsx" />;
    }
  };

  if (extractedResult) {
    return (
      <div className="max-w-6xl mx-auto p-6" data-id="5eo7mhkny" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <ExtractedQuestionsPreview
          result={extractedResult}
          onSaveQuestions={handleSaveExtractedQuestions}
          onClose={() => setExtractedResult(null)} data-id="7x3z2s3x3" data-path="src/components/questions/QuestionsPoolManager.tsx" />

      </div>);

  }

  const isAllSelected = filteredQuestions.length > 0 && selectedQuestions.length === filteredQuestions.length;
  const isIndeterminate = selectedQuestions.length > 0 && selectedQuestions.length < filteredQuestions.length;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-id="gh8s8tucj" data-path="src/components/questions/QuestionsPoolManager.tsx">
      <Card data-id="y7qktcrpv" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <CardHeader data-id="kdw6kkdw5" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <CardTitle className="flex items-center gap-2" data-id="ylddpnhl7" data-path="src/components/questions/QuestionsPoolManager.tsx">
            <FolderOpen className="h-5 w-5" data-id="htm739pjt" data-path="src/components/questions/QuestionsPoolManager.tsx" />
            Enhanced Questions Pool Manager
          </CardTitle>
          <CardDescription data-id="8if31m8xo" data-path="src/components/questions/QuestionsPoolManager.tsx">
            Manage your collection of questions with advanced categorization and AI-powered features.
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-id="ggdmc7uru" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <TabsList className="grid w-full grid-cols-4" data-id="ufl3rhimv" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <TabsTrigger value="pool" data-id="0rbth7alv" data-path="src/components/questions/QuestionsPoolManager.tsx">Questions Pool</TabsTrigger>
          <TabsTrigger value="categories" data-id="zzzvmel5t" data-path="src/components/questions/QuestionsPoolManager.tsx">Categories</TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center gap-1" data-id="hqq0jjbi4" data-path="src/components/questions/QuestionsPoolManager.tsx">
            {hasAIAccess('advanced') && <Crown className="h-3 w-3 text-amber-500" data-id="c8wp20dou" data-path="src/components/questions/QuestionsPoolManager.tsx" />}
            Extract from Files
          </TabsTrigger>
          <TabsTrigger value="weblink" className="flex items-center gap-1" data-id="oe9aa05ib" data-path="src/components/questions/QuestionsPoolManager.tsx">
            {hasAIAccess('advanced') && <Crown className="h-3 w-3 text-amber-500" data-id="52byw71co" data-path="src/components/questions/QuestionsPoolManager.tsx" />}
            Web Links
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pool" className="space-y-6" data-id="ykm66rcas" data-path="src/components/questions/QuestionsPoolManager.tsx">
          {/* Controls */}
          <Card data-id="eydrwyrb3" data-path="src/components/questions/QuestionsPoolManager.tsx">
            <CardContent className="pt-6" data-id="5f8a48qye" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <div className="flex flex-col md:flex-row gap-4 items-end" data-id="gc1q7lbod" data-path="src/components/questions/QuestionsPoolManager.tsx">
                <div className="flex-1 space-y-2" data-id="2vuonptlp" data-path="src/components/questions/QuestionsPoolManager.tsx">
                  <Label htmlFor="search" data-id="h0bs9wurr" data-path="src/components/questions/QuestionsPoolManager.tsx">Search Questions</Label>
                  <div className="relative" data-id="xq8mlaiz0" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="txsn3i7ny" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                    <Input
                      id="search"
                      placeholder="Search by question text, subject, or tags..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10" data-id="d44s7kasm" data-path="src/components/questions/QuestionsPoolManager.tsx" />

                  </div>
                </div>
                
                <div className="flex gap-2" data-id="1yv2vwpg1" data-path="src/components/questions/QuestionsPoolManager.tsx">
                  <Select value={filterSubject} onValueChange={setFilterSubject} data-id="ie1fab9nr" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <SelectTrigger className="w-40" data-id="tpkbvvgzi" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <SelectValue placeholder="Category" data-id="7xdt1aiyy" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="qvpweptkc" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <SelectItem value="all" data-id="wzfhdwh9b" data-path="src/components/questions/QuestionsPoolManager.tsx">All Categories</SelectItem>
                      <SelectItem value="Mathematics" data-id="6gzq2gbbb" data-path="src/components/questions/QuestionsPoolManager.tsx">Mathematics</SelectItem>
                      <SelectItem value="Science" data-id="u2i0qh1sh" data-path="src/components/questions/QuestionsPoolManager.tsx">Science</SelectItem>
                      <SelectItem value="History" data-id="mf95v5o4k" data-path="src/components/questions/QuestionsPoolManager.tsx">History</SelectItem>
                      <SelectItem value="English" data-id="097ibhxyn" data-path="src/components/questions/QuestionsPoolManager.tsx">English</SelectItem>
                      <SelectItem value="Geography" data-id="9jqrtzad3" data-path="src/components/questions/QuestionsPoolManager.tsx">Geography</SelectItem>
                      <SelectItem value="Physics" data-id="8519u1zbg" data-path="src/components/questions/QuestionsPoolManager.tsx">Physics</SelectItem>
                      <SelectItem value="Chemistry" data-id="o4ubrwwpi" data-path="src/components/questions/QuestionsPoolManager.tsx">Chemistry</SelectItem>
                      <SelectItem value="Biology" data-id="mm7yeqqt9" data-path="src/components/questions/QuestionsPoolManager.tsx">Biology</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterDifficulty} onValueChange={setFilterDifficulty} data-id="k39ginel1" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <SelectTrigger className="w-32" data-id="lmwmoyel2" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <SelectValue placeholder="Difficulty" data-id="ew69u91ex" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="buo90c7ro" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <SelectItem value="all" data-id="acllqbasy" data-path="src/components/questions/QuestionsPoolManager.tsx">All Levels</SelectItem>
                      <SelectItem value="easy" data-id="0kxt0zobl" data-path="src/components/questions/QuestionsPoolManager.tsx">Easy</SelectItem>
                      <SelectItem value="medium" data-id="ba6wd4xt1" data-path="src/components/questions/QuestionsPoolManager.tsx">Medium</SelectItem>
                      <SelectItem value="hard" data-id="mzoxgdi3q" data-path="src/components/questions/QuestionsPoolManager.tsx">Hard</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterType} onValueChange={setFilterType} data-id="1522esvpz" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <SelectTrigger className="w-40" data-id="s4tzyqi52" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <SelectValue placeholder="Type" data-id="24hhrhf7a" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="8fgysg5gn" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <SelectItem value="all" data-id="3u6nfbsx8" data-path="src/components/questions/QuestionsPoolManager.tsx">All Types</SelectItem>
                      <SelectItem value="multiple_choice" data-id="mpdg2v0t1" data-path="src/components/questions/QuestionsPoolManager.tsx">Multiple Choice</SelectItem>
                      <SelectItem value="true_false" data-id="du013zwt4" data-path="src/components/questions/QuestionsPoolManager.tsx">True/False</SelectItem>
                      <SelectItem value="short_answer" data-id="nz7y2rjqb" data-path="src/components/questions/QuestionsPoolManager.tsx">Short Answer</SelectItem>
                      <SelectItem value="essay" data-id="kxan6oj5n" data-path="src/components/questions/QuestionsPoolManager.tsx">Essay</SelectItem>
                    </SelectContent>
                  </Select>

                  <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} data-id="st4q6z6i3" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <DialogTrigger asChild data-id="evotgysg5" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <Button onClick={resetForm} data-id="ne393yl0g" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        <Plus className="h-4 w-4 mr-2" data-id="35bh0eq3u" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                        Add Question
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-id="7vku7zc7l" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <DialogHeader data-id="uzsvzm15o" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        <DialogTitle data-id="3eiov1gru" data-path="src/components/questions/QuestionsPoolManager.tsx">Add New Question</DialogTitle>
                        <DialogDescription data-id="drfh6in4v" data-path="src/components/questions/QuestionsPoolManager.tsx">
                          Create a new question for your questions pool.
                        </DialogDescription>
                      </DialogHeader>
                      <QuestionForm
                        formData={formData}
                        setFormData={setFormData}
                        onSubmit={handleAddQuestion}
                        onCancel={() => setIsAddDialogOpen(false)} data-id="5jcd25hwi" data-path="src/components/questions/QuestionsPoolManager.tsx" />

                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selection Controls */}
          {filteredQuestions.length > 0 &&
          <Card data-id="76ax27cdq" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <CardContent className="pt-6" data-id="yge035uuu" data-path="src/components/questions/QuestionsPoolManager.tsx">
                <div className="flex items-center justify-between" data-id="98zjfy1mm" data-path="src/components/questions/QuestionsPoolManager.tsx">
                  <div className="flex items-center gap-4" data-id="85o8dqy73" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <div className="flex items-center space-x-2" data-id="zfq09csft" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <Checkbox
                      id="select-all"
                      checked={isAllSelected}
                      ref={(ref) => {
                        if (ref) ref.indeterminate = isIndeterminate;
                      }}
                      onCheckedChange={handleSelectAll} data-id="3ihmnvf56" data-path="src/components/questions/QuestionsPoolManager.tsx" />

                      <Label htmlFor="select-all" className="text-sm font-medium" data-id="1838fj94i" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        Select All ({filteredQuestions.length})
                      </Label>
                    </div>
                    {selectedQuestions.length > 0 &&
                  <Badge variant="secondary" data-id="zytnv92va" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        {selectedQuestions.length} selected
                      </Badge>
                  }
                  </div>
                  
                  {selectedQuestions.length > 0 &&
                <div className="flex items-center gap-2" data-id="30tm1n5ly" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedQuestions([])} data-id="3474lm0hc" data-path="src/components/questions/QuestionsPoolManager.tsx">

                        <X className="h-4 w-4 mr-1" data-id="1sfqegumf" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                        Clear Selection
                      </Button>
                      <AlertDialog open={isDeleteMultipleOpen} onOpenChange={setIsDeleteMultipleOpen} data-id="g3h8it8nz" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        <AlertDialogTrigger asChild data-id="pby6s4j98" data-path="src/components/questions/QuestionsPoolManager.tsx">
                          <Button variant="destructive" size="sm" data-id="p86g7avev" data-path="src/components/questions/QuestionsPoolManager.tsx">
                            <Trash2 className="h-4 w-4 mr-1" data-id="9l67ln3jx" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                            Delete Multiple ({selectedQuestions.length})
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-id="1v8ponnln" data-path="src/components/questions/QuestionsPoolManager.tsx">
                          <AlertDialogHeader data-id="9utsh1p26" data-path="src/components/questions/QuestionsPoolManager.tsx">
                            <AlertDialogTitle data-id="8gu6vufnl" data-path="src/components/questions/QuestionsPoolManager.tsx">Delete Multiple Questions</AlertDialogTitle>
                            <AlertDialogDescription data-id="1i4s7rayq" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              Are you sure you want to delete {selectedQuestions.length} selected questions? 
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter data-id="nslynm7ul" data-path="src/components/questions/QuestionsPoolManager.tsx">
                            <AlertDialogCancel disabled={isDeletingMultiple} data-id="abw06muty" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                          onClick={handleDeleteMultiple}
                          disabled={isDeletingMultiple}
                          className="bg-red-600 hover:bg-red-700" data-id="2imduptb1" data-path="src/components/questions/QuestionsPoolManager.tsx">

                              {isDeletingMultiple ? 'Deleting...' : `Delete ${selectedQuestions.length} Questions`}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                }
                </div>
              </CardContent>
            </Card>
          }

          {/* Questions List */}
          {loading ?
          <Card data-id="qnk7zww4b" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <CardContent className="pt-6 text-center" data-id="pafbtiqnd" data-path="src/components/questions/QuestionsPoolManager.tsx">
                <p data-id="ewt0xylvb" data-path="src/components/questions/QuestionsPoolManager.tsx">Loading questions...</p>
              </CardContent>
            </Card> :
          filteredQuestions.length === 0 ?
          <Card data-id="7pihmwmg7" data-path="src/components/questions/QuestionsPoolManager.tsx">
                <CardContent className="pt-6 text-center text-gray-500" data-id="k4mkruol8" data-path="src/components/questions/QuestionsPoolManager.tsx">
                  <p data-id="x70y2k2jj" data-path="src/components/questions/QuestionsPoolManager.tsx">No questions found. Add some questions to get started.</p>
                </CardContent>
              </Card> :

          <div className="space-y-4" data-id="rs0c9t2qw" data-path="src/components/questions/QuestionsPoolManager.tsx">
                {filteredQuestions.map((question) =>
            <Card key={question.id} className={selectedQuestions.includes(question.id) ? 'ring-2 ring-blue-200 bg-blue-50/50' : ''} data-id="wlx1idbn2" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <CardContent className="pt-6" data-id="5lrlqdixv" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <div className="flex justify-between items-start mb-4" data-id="590sa017o" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        <div className="flex items-start gap-3 flex-1" data-id="mx6sxf6qn" data-path="src/components/questions/QuestionsPoolManager.tsx">
                          <Checkbox
                      checked={selectedQuestions.includes(question.id)}
                      onCheckedChange={(checked) => handleSelectQuestion(question.id, checked as boolean)}
                      className="mt-1" data-id="yp08e9rk8" data-path="src/components/questions/QuestionsPoolManager.tsx" />

                          <div className="flex-1" data-id="45iqtcchx" data-path="src/components/questions/QuestionsPoolManager.tsx">
                            <div className="flex items-center gap-2 mb-2" data-id="93kncvh5g" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              {getSourceIcon(question.source_type)}
                              <Badge className={getQuestionTypeColor(question.question_type)} data-id="7u0nx189v" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                {question.question_type.replace('_', ' ')}
                              </Badge>
                              <Badge className={getDifficultyColor(question.difficulty)} data-id="a6nnswirk" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                {question.difficulty}
                              </Badge>
                              <Badge variant="outline" data-id="zp619mtrz" data-path="src/components/questions/QuestionsPoolManager.tsx">{question.subject}</Badge>
                            </div>
                            <h3 className="font-medium mb-2" data-id="gzse11wqt" data-path="src/components/questions/QuestionsPoolManager.tsx">{question.question_text}</h3>
                            <p className="text-sm text-gray-600 mb-2" data-id="4noq8o8xi" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              <strong data-id="ygr3ktt36" data-path="src/components/questions/QuestionsPoolManager.tsx">Answer:</strong> {question.answer_text}
                            </p>
                            {question.tags.length > 0 &&
                      <div className="flex gap-1 mb-2" data-id="vnmo2jf3h" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              {question.tags.map((tag, index) =>
                        <Badge key={index} variant="secondary" className="text-xs" data-id="76xa3vs8b" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                  {tag}
                                </Badge>
                        )}
                            </div>
                      }
                            <p className="text-xs text-gray-500" data-id="0b5xa5m1f" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              Created: {new Date(question.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2" data-id="ptilt0wb5" data-path="src/components/questions/QuestionsPoolManager.tsx">
                          <Dialog data-id="heclwxrhj" data-path="src/components/questions/QuestionsPoolManager.tsx">
                            <DialogTrigger asChild data-id="zr48gedv3" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(question)} data-id="fsh1qusxn" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                <Edit className="h-4 w-4" data-id="ulcscms44" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-id="ml17fw6p5" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              <DialogHeader data-id="8qa7ni3kk" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                <DialogTitle data-id="wzl7f8nwk" data-path="src/components/questions/QuestionsPoolManager.tsx">Edit Question</DialogTitle>
                                <DialogDescription data-id="mwt04u5de" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                  Update the question details.
                                </DialogDescription>
                              </DialogHeader>
                              <QuestionForm
                          formData={formData}
                          setFormData={setFormData}
                          onSubmit={handleEditQuestion}
                          onCancel={() => setEditingQuestion(null)}
                          isEditing={true} data-id="phawugalv" data-path="src/components/questions/QuestionsPoolManager.tsx" />

                            </DialogContent>
                          </Dialog>

                          <AlertDialog data-id="0tzwnucf4" data-path="src/components/questions/QuestionsPoolManager.tsx">
                            <AlertDialogTrigger asChild data-id="hifsaf6rw" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              <Button variant="ghost" size="sm" data-id="f402spmt4" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                <Trash2 className="h-4 w-4" data-id="kjfpcy2e5" data-path="src/components/questions/QuestionsPoolManager.tsx" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-id="0u1jpms1k" data-path="src/components/questions/QuestionsPoolManager.tsx">
                              <AlertDialogHeader data-id="qlfqcgz9i" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                <AlertDialogTitle data-id="th3c8u2d6" data-path="src/components/questions/QuestionsPoolManager.tsx">Delete Question</AlertDialogTitle>
                                <AlertDialogDescription data-id="oy9txv0kb" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                  Are you sure you want to delete this question? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter data-id="5435jfhjh" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                <AlertDialogCancel data-id="7litjkfev" data-path="src/components/questions/QuestionsPoolManager.tsx">Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteQuestion(question.id)} data-id="sm1stmn6e" data-path="src/components/questions/QuestionsPoolManager.tsx">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
            )}

                {/* Pagination */}
                {totalPages > 1 &&
            <Card data-id="wrlb24bu8" data-path="src/components/questions/QuestionsPoolManager.tsx">
                    <CardContent className="pt-6" data-id="c53elr700" data-path="src/components/questions/QuestionsPoolManager.tsx">
                      <div className="flex justify-center gap-2" data-id="j3cbjv8d3" data-path="src/components/questions/QuestionsPoolManager.tsx">
                        <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1} data-id="8gkfkwpxp" data-path="src/components/questions/QuestionsPoolManager.tsx">

                          Previous
                        </Button>
                        <span className="flex items-center px-4" data-id="tholz555b" data-path="src/components/questions/QuestionsPoolManager.tsx">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages} data-id="uq4jwejvb" data-path="src/components/questions/QuestionsPoolManager.tsx">

                          Next
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
            }
              </div>
          }
        </TabsContent>

        <TabsContent value="categories" data-id="ziddtpw6h" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <CategoryManager
            onCategoriesChange={fetchQuestions}
            selectedCategory={filterSubject}
            onCategorySelect={setFilterSubject} data-id="lakxo7vfa" data-path="src/components/questions/QuestionsPoolManager.tsx" />

        </TabsContent>

        <TabsContent value="upload" data-id="dofvmfyw9" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <FileUploadSection
            onQuestionsExtracted={setExtractedResult}
            subject={filterSubject}
            onSubjectChange={setFilterSubject} data-id="i08a10ugo" data-path="src/components/questions/QuestionsPoolManager.tsx" />

        </TabsContent>

        <TabsContent value="weblink" data-id="z8bgcwh7d" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <WebLinkSection
            onQuestionsExtracted={setExtractedResult}
            subject={filterSubject}
            onSubjectChange={setFilterSubject} data-id="brf75ru8i" data-path="src/components/questions/QuestionsPoolManager.tsx" />

        </TabsContent>
      </Tabs>
    </div>);

};

// Question Form Component
interface QuestionFormProps {
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  isEditing = false
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-id="q6ebjloa5" data-path="src/components/questions/QuestionsPoolManager.tsx">
      <div className="space-y-2" data-id="ep8wbe30p" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <Label htmlFor="question_text" data-id="wiybuw8q3" data-path="src/components/questions/QuestionsPoolManager.tsx">Question Text *</Label>
        <Textarea
          id="question_text"
          value={formData.question_text}
          onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
          required
          rows={3}
          placeholder="Enter the question..." data-id="v7pitkosk" data-path="src/components/questions/QuestionsPoolManager.tsx" />

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="vo5gypeeg" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <div className="space-y-2" data-id="k68pb3x6n" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <Label htmlFor="question_type" data-id="i9dr3n3kh" data-path="src/components/questions/QuestionsPoolManager.tsx">Question Type *</Label>
          <Select
            value={formData.question_type}
            onValueChange={(value) => setFormData({ ...formData, question_type: value })} data-id="t41cp42p4" data-path="src/components/questions/QuestionsPoolManager.tsx">

            <SelectTrigger data-id="372nvj8oo" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <SelectValue data-id="7ylbhum97" data-path="src/components/questions/QuestionsPoolManager.tsx" />
            </SelectTrigger>
            <SelectContent data-id="wtd5shqfa" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <SelectItem value="multiple_choice" data-id="1epvwqb61" data-path="src/components/questions/QuestionsPoolManager.tsx">Multiple Choice</SelectItem>
              <SelectItem value="true_false" data-id="u1kessy28" data-path="src/components/questions/QuestionsPoolManager.tsx">True/False</SelectItem>
              <SelectItem value="short_answer" data-id="f5fmtfg9z" data-path="src/components/questions/QuestionsPoolManager.tsx">Short Answer</SelectItem>
              <SelectItem value="essay" data-id="wvfhisp2i" data-path="src/components/questions/QuestionsPoolManager.tsx">Essay</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2" data-id="1c3x8c6p0" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <Label htmlFor="difficulty" data-id="qwv83fg2z" data-path="src/components/questions/QuestionsPoolManager.tsx">Difficulty *</Label>
          <Select
            value={formData.difficulty}
            onValueChange={(value) => setFormData({ ...formData, difficulty: value })} data-id="0d42b3s3g" data-path="src/components/questions/QuestionsPoolManager.tsx">

            <SelectTrigger data-id="o8ej13ztp" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <SelectValue data-id="d8mca0bsl" data-path="src/components/questions/QuestionsPoolManager.tsx" />
            </SelectTrigger>
            <SelectContent data-id="17a69hhti" data-path="src/components/questions/QuestionsPoolManager.tsx">
              <SelectItem value="easy" data-id="imvsm2pnt" data-path="src/components/questions/QuestionsPoolManager.tsx">Easy</SelectItem>
              <SelectItem value="medium" data-id="qqp28mdqy" data-path="src/components/questions/QuestionsPoolManager.tsx">Medium</SelectItem>
              <SelectItem value="hard" data-id="9ekuekalc" data-path="src/components/questions/QuestionsPoolManager.tsx">Hard</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="24roelt4h" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <div className="space-y-2" data-id="opchcyvk8" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <Label htmlFor="subject" data-id="8ztnv7yt3" data-path="src/components/questions/QuestionsPoolManager.tsx">Category/Subject *</Label>
          <Input
            id="subject"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            required
            placeholder="e.g., Mathematics, Science" data-id="rxcsf9pav" data-path="src/components/questions/QuestionsPoolManager.tsx" />

        </div>

        <div className="space-y-2" data-id="zh7fj6ol7" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <Label htmlFor="tags" data-id="zjkcxjy63" data-path="src/components/questions/QuestionsPoolManager.tsx">Tags (comma-separated)</Label>
          <Input
            id="tags"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            placeholder="e.g., algebra, equations, basic" data-id="wuw7gaujy" data-path="src/components/questions/QuestionsPoolManager.tsx" />

        </div>
      </div>

      {formData.question_type === 'multiple_choice' &&
      <div className="space-y-2" data-id="mlm1f73x5" data-path="src/components/questions/QuestionsPoolManager.tsx">
          <Label data-id="t6o3hxws9" data-path="src/components/questions/QuestionsPoolManager.tsx">Answer Options *</Label>
          {formData.options.map((option: string, index: number) =>
        <Input
          key={index}
          value={option}
          onChange={(e) => {
            const newOptions = [...formData.options];
            newOptions[index] = e.target.value;
            setFormData({ ...formData, options: newOptions });
          }}
          placeholder={`Option ${index + 1}`}
          required={index < 2} // First two options are required
          data-id="x29jqx1qu" data-path="src/components/questions/QuestionsPoolManager.tsx" />
        )}
        </div>
      }

      <div className="space-y-2" data-id="0j5ktvtox" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <Label htmlFor="answer_text" data-id="lju7r6lti" data-path="src/components/questions/QuestionsPoolManager.tsx">
          {formData.question_type === 'multiple_choice' ? 'Correct Answer *' : 'Answer/Explanation *'}
        </Label>
        <Textarea
          id="answer_text"
          value={formData.answer_text}
          onChange={(e) => setFormData({ ...formData, answer_text: e.target.value })}
          required
          rows={2}
          placeholder={
          formData.question_type === 'multiple_choice' ?
          "Enter the correct answer from the options above" :
          "Enter the answer or explanation"
          } data-id="go2d4864p" data-path="src/components/questions/QuestionsPoolManager.tsx" />

      </div>

      <div className="flex justify-end gap-2" data-id="r3fl1zar6" data-path="src/components/questions/QuestionsPoolManager.tsx">
        <Button type="button" variant="outline" onClick={onCancel} data-id="n691goif3" data-path="src/components/questions/QuestionsPoolManager.tsx">
          Cancel
        </Button>
        <Button type="submit" data-id="4y4r8ih2t" data-path="src/components/questions/QuestionsPoolManager.tsx">
          {isEditing ? 'Update Question' : 'Add Question'}
        </Button>
      </div>
    </form>);

};

export default QuestionsPoolManager;